import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export default function EducationDashboard() {
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notaries, setNotaries] = useState([]);
  const [manualProgress, setManualProgress] = useState([]);
  const [academyProgress, setAcademyProgress] = useState([]);
  const [examProgress, setExamProgress] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [reviews, setReviews] = useState([]);

  // Track which tables are missing so we can show friendly notices
  const [missingTables, setMissingTables] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  // Safe query helper — never throws, returns [] if table missing
  const safeQuery = async (tableName, queryFn) => {
    try {
      const { data, error } = await queryFn();
      if (error) {
        console.warn(`[EducationDashboard] ${tableName} query error:`, error.message);
        // Postgres error code 42P01 = undefined_table
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          setMissingTables((prev) =>
            prev.includes(tableName) ? prev : [...prev, tableName]
          );
        }
        return [];
      }
      return data || [];
    } catch (err) {
      console.warn(`[EducationDashboard] ${tableName} threw:`, err.message);
      setMissingTables((prev) =>
        prev.includes(tableName) ? prev : [...prev, tableName]
      );
      return [];
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [notariesData, manualData, academyData, examData, tracksData, chaptersData, reviewsData] = await Promise.all([
        // ✅ FIX: Filter to ONLY notaries (was showing all profiles before!)
        safeQuery('profiles', () =>
          supabase
            .from('profiles')
            .select('id, full_name, email')
            .eq('role', 'notary')
            .order('full_name', { ascending: true })
        ),
        // Training Manual progress
        safeQuery('manual_progress', () =>
          supabase.from('manual_progress').select('*')
        ),
        // ✅ FIX: Use academy_progress (12 real rows!) instead of academy_enrollments (0 rows / wrong table!)
        safeQuery('academy_progress', () =>
          supabase.from('academy_progress').select('*')
        ),
        // ✅ NEW: Final exam progress — to show who graduated!!
        safeQuery('academy_final_exam_progress', () =>
          supabase.from('academy_final_exam_progress').select('*')
        ),
        // ✅ NEW: Tracks — so we can show course NAMES instead of UUIDs
        safeQuery('academy_tracks', () =>
          supabase
            .from('academy_tracks')
            .select('id, title, icon, color, published')
            .order('sort_order', { ascending: true })
        ),
        // Training manual chapters
        safeQuery('resources_chapters', () =>
          supabase
            .from('resources_chapters')
            .select('id, title, sort_order')
            .order('sort_order', { ascending: true })
        ),
        // ✅ NEW: Course reviews & feedback from notaries
        safeQuery('academy_track_reviews', () =>
          supabase
            .from('academy_track_reviews')
            .select('*')
            .order('created_at', { ascending: false })
        ),
      ]);

      setNotaries(notariesData);
      setManualProgress(manualData);
      setAcademyProgress(academyData);
      setExamProgress(examData);
      setTracks(tracksData);
      setChapters(chaptersData);
      setReviews(reviewsData);
    } catch (err) {
      console.error('[EducationDashboard] Unexpected error:', err);
      setError('Could not load education data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  // --- Helpers ---------------------------------------------------------

  const getManualProgressForNotary = (notaryId) => {
    const rows = manualProgress.filter((r) => r.notary_id === notaryId);
    const total = chapters.length || 13;
    const completed = rows.length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percent };
  };

  // ✅ ENHANCED: Now returns rich data per track (modules passed, exam status, scores)
  const getAcademyProgressForNotary = (notaryId) => {
    // Lessons/modules passed by this notary
    const myProgress = academyProgress.filter((r) => r.notary_id === notaryId);
    // Final exam attempts by this notary
    const myExams = examProgress.filter((r) => r.notary_id === notaryId);

    // Build per-track summary
    const trackSummaries = tracks
      .filter((t) => t.published) // only show published tracks
      .map((t) => {
        const modulesPassed = myProgress.filter(
          (p) => p.track_id === t.id && p.quiz_passed === true
        ).length;
        const exam = myExams.find((e) => e.track_id === t.id);
        const examPassed = exam?.passed === true;
        const examScore = exam?.score ?? null;
        const examAttempts = exam?.attempts ?? 0;
        const passedAt = exam?.passed_at || null;

        return {
          trackId: t.id,
          trackTitle: t.title,
          trackIcon: t.icon || '🎓',
          trackColor: t.color || '#059669',
          modulesPassed,
          examPassed,
          examScore,
          examAttempts,
          passedAt,
        };
      });

    const totalModulesPassed = myProgress.filter((p) => p.quiz_passed === true).length;
    const totalExamsPassed = myExams.filter((e) => e.passed === true).length;

    return {
      trackSummaries,
      totalModulesPassed,
      totalExamsPassed,
    };
  };

  const getLeaderboard = () => {
    return notaries
      .map((n) => {
        const manual = getManualProgressForNotary(n.id);
        const academy = getAcademyProgressForNotary(n.id);
        // Updated scoring: chapter read = 5pts, module passed = 10pts, final exam passed = 50pts
        const score =
          manual.completed * 5 +
          academy.totalModulesPassed * 10 +
          academy.totalExamsPassed * 50;
        return {
          id: n.id,
          name: n.full_name || n.email || 'Unnamed',
          manualPercent: manual.percent,
          academyModulesPassed: academy.totalModulesPassed,
          academyExamsPassed: academy.totalExamsPassed,
          score,
        };
      })
      .sort((a, b) => b.score - a.score);
  };

  // --- Review helpers --------------------------------------------------

  const getNotaryName = (notaryId) => {
    const n = notaries.find((x) => x.id === notaryId);
    return n ? (n.full_name || n.email || 'Unnamed') : 'Unknown notary';
  };

  const getTrackTitle = (trackId) => {
    const t = tracks.find((x) => x.id === trackId);
    return t ? t.title : 'Unknown track';
  };

  const getTrackIcon = (trackId) => {
    const t = tracks.find((x) => x.id === trackId);
    return t ? (t.icon || '🎓') : '🎓';
  };

  // Per-track rating rollup: avg stars, count, recommend rate
  const getReviewSummaries = () => {
    const byTrack = {};
    reviews.forEach((r) => {
      if (!byTrack[r.track_id]) byTrack[r.track_id] = [];
      byTrack[r.track_id].push(r);
    });
    return Object.entries(byTrack).map(([trackId, list]) => {
      const count = list.length;
      const avg = count > 0 ? list.reduce((s, r) => s + (r.rating || 0), 0) / count : 0;
      const recommendCount = list.filter((r) => r.would_recommend !== false).length;
      return {
        trackId,
        title: getTrackTitle(trackId),
        icon: getTrackIcon(trackId),
        count,
        avg,
        recommendPct: count > 0 ? Math.round((recommendCount / count) * 100) : 0,
      };
    });
  };

  // --- Styles ----------------------------------------------------------

  const styles = {
    container: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    header: { marginBottom: '24px' },
    title: { fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    notice: {
      background: '#fef3c7',
      border: '1px solid #fcd34d',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '16px',
      color: '#92400e',
      fontSize: '14px',
    },
    statsBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '12px',
      marginBottom: '20px',
    },
    statCard: {
      background: '#151715',
      borderRadius: '10px',
      padding: '14px 18px',
      color: '#4D4E8D',
    },
    statLabel: {
      fontSize: '10px',
      letterSpacing: '0.08em',
      fontWeight: '700',
      color: 'rgba(77,78,141,0.7)',
    },
    statValue: {
      fontSize: '22px',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      fontWeight: '500',
      marginTop: '4px',
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      borderBottom: '2px solid #e5e7eb',
      marginBottom: '24px',
      flexWrap: 'wrap',
    },
    tab: {
      padding: '12px 20px',
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      fontSize: '15px',
      fontWeight: '600',
      color: '#6b7280',
      borderBottom: '3px solid transparent',
      marginBottom: '-2px',
    },
    tabActive: {
      color: '#059669',
      borderBottom: '3px solid #059669',
    },
    card: {
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '12px',
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap',
    },
    name: { fontSize: '16px', fontWeight: '600', color: '#1f2937' },
    meta: { fontSize: '13px', color: '#6b7280' },
    progressBarOuter: {
      flex: 1,
      height: '8px',
      background: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
      minWidth: '120px',
    },
    progressBarInner: (pct) => ({
      width: `${pct}%`,
      height: '100%',
      background: pct >= 100 ? '#059669' : '#10b981',
      transition: 'width 0.3s',
    }),
    rank: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#059669',
      minWidth: '40px',
    },
    empty: {
      textAlign: 'center',
      padding: '40px',
      color: '#6b7280',
      background: '#f9fafb',
      borderRadius: '12px',
    },
    loading: { textAlign: 'center', padding: '40px', color: '#6b7280' },
    errorBox: {
      background: '#fee2e2',
      border: '1px solid #fca5a5',
      color: '#991b1b',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    trackPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '600',
      marginRight: '6px',
      marginTop: '4px',
    },
    examBadge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '10px',
      fontWeight: '700',
      marginLeft: '6px',
    },
    examPassed: {
      background: '#d1fae5',
      color: '#065f46',
    },
    examPending: {
      background: '#fef3c7',
      color: '#92400e',
    },
    examNotStarted: {
      background: '#e5e7eb',
      color: '#6b7280',
    },
  };

  // --- Computed totals for stats bar ---
  const totalNotaries = notaries.length;
  const totalManualReads = manualProgress.length;
  const totalAcademyModulesPassed = academyProgress.filter((p) => p.quiz_passed === true).length;
  const totalExamsPassed = examProgress.filter((e) => e.passed === true).length;
  const totalReviews = reviews.length;
  const avgRatingAll = totalReviews > 0
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / totalReviews).toFixed(1)
    : '—';

  // --- Render ----------------------------------------------------------

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading education data…</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📚 Education Dashboard</h1>
        <div style={styles.subtitle}>
          Notary training progress across Manual, Academy, and Leaderboard
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {missingTables.length > 0 && (
        <div style={styles.notice}>
          ⚠️ Some data is unavailable because these tables are missing or empty:{' '}
          <strong>{missingTables.join(', ')}</strong>. The dashboard is still
          working — just showing zeros where data isn't available yet.
        </div>
      )}

      {/* ✅ NEW: Top stats bar — birds eye view of all education activity */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>NOTARIES</div>
          <div style={styles.statValue}>{totalNotaries}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>MANUAL CHAPTERS READ</div>
          <div style={styles.statValue}>{totalManualReads}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>ACADEMY MODULES PASSED</div>
          <div style={styles.statValue}>{totalAcademyModulesPassed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>FINAL EXAMS PASSED</div>
          <div style={styles.statValue}>{totalExamsPassed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>AVG COURSE RATING</div>
          <div style={styles.statValue}>{avgRatingAll}{totalReviews > 0 ? ' ★' : ''}</div>
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'manual' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('manual')}
        >
          📖 Training Manual
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'academy' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('academy')}
        >
          🎓 Academy
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'leaderboard' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('leaderboard')}
        >
          🏆 Leaderboard
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'reviews' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('reviews')}
        >
          ⭐ Reviews
        </button>
      </div>

      {/* MANUAL TAB */}
      {activeTab === 'manual' && (
        <div>
          {notaries.length === 0 ? (
            <div style={styles.empty}>No notaries found yet.</div>
          ) : (
            notaries.map((n) => {
              const p = getManualProgressForNotary(n.id);
              return (
                <div key={n.id} style={styles.card}>
                  <div style={styles.row}>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={styles.name}>
                        {n.full_name || n.email || 'Unnamed'}
                      </div>
                      <div style={styles.meta}>
                        {p.completed} of {p.total} chapters read
                      </div>
                    </div>
                    <div style={styles.progressBarOuter}>
                      <div style={styles.progressBarInner(p.percent)} />
                    </div>
                    <div style={{ ...styles.meta, minWidth: '40px', textAlign: 'right' }}>
                      {p.percent}%
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ✅ ENHANCED ACADEMY TAB — Now shows per-track progress + exam status */}
      {activeTab === 'academy' && (
        <div>
          {notaries.length === 0 ? (
            <div style={styles.empty}>No notaries found yet.</div>
          ) : tracks.length === 0 ? (
            <div style={styles.empty}>
              No published academy tracks yet. Create one in Academy CMS!
            </div>
          ) : (
            notaries.map((n) => {
              const a = getAcademyProgressForNotary(n.id);
              const hasAnyActivity =
                a.totalModulesPassed > 0 || a.totalExamsPassed > 0;
              return (
                <div key={n.id} style={styles.card}>
                  <div style={styles.row}>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={styles.name}>
                        {n.full_name || n.email || 'Unnamed'}
                      </div>
                      <div style={styles.meta}>
                        {hasAnyActivity ? (
                          <>
                            {a.totalModulesPassed} module{a.totalModulesPassed !== 1 ? 's' : ''} passed
                            {' • '}
                            {a.totalExamsPassed} exam{a.totalExamsPassed !== 1 ? 's' : ''} passed
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            Not started yet
                          </span>
                        )}
                      </div>
                      {/* Per-track breakdown pills */}
                      <div style={{ marginTop: '10px' }}>
                        {a.trackSummaries.map((t) => {
                          const examStyle = t.examPassed
                            ? styles.examPassed
                            : t.examAttempts > 0
                            ? styles.examPending
                            : styles.examNotStarted;
                          const examLabel = t.examPassed
                            ? `🏆 Passed ${t.examScore != null ? `(${t.examScore}%)` : ''}`
                            : t.examAttempts > 0
                            ? `${t.examAttempts} attempt${t.examAttempts !== 1 ? 's' : ''}`
                            : 'No exam yet';
                          return (
                            <span
                              key={t.trackId}
                              style={{
                                ...styles.trackPill,
                                background: t.modulesPassed > 0 || t.examPassed
                                  ? '#f3f4f6'
                                  : '#fafafa',
                                color: t.modulesPassed > 0 || t.examPassed
                                  ? '#1f2937'
                                  : '#9ca3af',
                                border: `1px solid ${t.modulesPassed > 0 || t.examPassed ? '#d1d5db' : '#e5e7eb'}`,
                              }}
                            >
                              <span>{t.trackIcon}</span>
                              <span>{t.trackTitle}</span>
                              <span style={{ color: '#6b7280', marginLeft: '4px' }}>
                                {t.modulesPassed} mod
                              </span>
                              <span style={{ ...styles.examBadge, ...examStyle }}>
                                {examLabel}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ✅ ENHANCED LEADERBOARD TAB */}
      {activeTab === 'leaderboard' && (
        <div>
          {notaries.length === 0 ? (
            <div style={styles.empty}>No notaries found yet.</div>
          ) : (
            getLeaderboard().map((n, idx) => (
              <div key={n.id} style={styles.card}>
                <div style={styles.row}>
                  <div style={styles.rank}>
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                  </div>
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div style={styles.name}>{n.name}</div>
                    <div style={styles.meta}>
                      Manual {n.manualPercent}% • {n.academyModulesPassed} module
                      {n.academyModulesPassed !== 1 ? 's' : ''} • {n.academyExamsPassed} exam
                      {n.academyExamsPassed !== 1 ? 's' : ''} 🏆
                    </div>
                  </div>
                  <div style={{ ...styles.rank, color: '#1f2937' }}>{n.score} pts</div>
                </div>
              </div>
            ))
          )}
          {notaries.length > 0 && (
            <div style={{ ...styles.meta, textAlign: 'center', marginTop: '16px', fontSize: '11px' }}>
              Scoring: Manual chapter = 5pts • Academy module = 10pts • Final exam = 50pts
            </div>
          )}
        </div>
      )}

      {/* ⭐ REVIEWS TAB */}
      {activeTab === 'reviews' && (
        <div>
          {reviews.length === 0 ? (
            <div style={styles.empty}>
              No course reviews yet. Reviews appear here after notaries pass a final exam and rate the course.
            </div>
          ) : (
            <>
              {/* Per-track rollup summary */}
              <div style={{ marginBottom: '20px' }}>
                {getReviewSummaries().map((s) => (
                  <div key={s.trackId} style={styles.card}>
                    <div style={styles.row}>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <div style={styles.name}>
                          {s.icon} {s.title}
                        </div>
                        <div style={styles.meta}>
                          {s.count} review{s.count !== 1 ? 's' : ''} • {s.recommendPct}% would recommend
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', color: '#4D4E8D', fontWeight: 700 }}>
                          {'★'.repeat(Math.round(s.avg))}
                          <span style={{ color: '#e5e7eb' }}>{'★'.repeat(5 - Math.round(s.avg))}</span>
                        </div>
                        <div style={{ ...styles.meta, textAlign: 'right' }}>{s.avg.toFixed(1)} avg</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Individual reviews */}
              <div style={{ ...styles.meta, fontWeight: 700, color: '#151715', marginBottom: '10px', fontSize: '12px', letterSpacing: '0.04em' }}>
                ALL REVIEWS ({reviews.length})
              </div>
              {reviews.map((r) => (
                <div key={r.id} style={styles.card}>
                  <div style={{ ...styles.row, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={styles.name}>{getNotaryName(r.notary_id)}</div>
                      <div style={{ ...styles.meta, marginBottom: '6px' }}>
                        {getTrackIcon(r.track_id)} {getTrackTitle(r.track_id)}
                        {r.created_at ? ` • ${new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}
                      </div>
                      {r.review_text && (
                        <div style={{ fontSize: '13px', color: '#3A3D40', fontStyle: 'italic', lineHeight: 1.6, marginTop: '4px' }}>
                          "{r.review_text}"
                        </div>
                      )}
                      <div style={{ ...styles.meta, marginTop: '6px' }}>
                        {r.would_recommend !== false ? '👍 Would recommend' : '👎 Would not recommend'}
                      </div>
                    </div>
                    <div style={{ fontSize: '16px', color: '#4D4E8D', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {'★'.repeat(r.rating || 0)}
                      <span style={{ color: '#e5e7eb' }}>{'★'.repeat(5 - (r.rating || 0))}</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
